/**
 * Toast notification system.
 *
 * Usage:
 *   1. Wrap your app in <ToastProvider> (already done in App.tsx).
 *   2. Call the hook inside any component:
 *        const { toast } = useToast()
 *        toast.success('Agent created!')
 *        toast.error('Failed to delete agent')
 *        toast.info('Processing…')
 *        toast.warning('Quota nearly exhausted')
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ToastVariant = 'success' | 'error' | 'info' | 'warning'

interface ToastItem {
  id: string
  variant: ToastVariant
  title: string
  message?: string
  /** Auto-dismiss delay in ms (default 4000). Pass 0 to keep until dismissed. */
  duration?: number
}

interface ToastContextValue {
  toast: {
    success: (title: string, message?: string, duration?: number) => void
    error: (title: string, message?: string, duration?: number) => void
    info: (title: string, message?: string, duration?: number) => void
    warning: (title: string, message?: string, duration?: number) => void
  }
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const ToastContext = createContext<ToastContextValue | null>(null)

// ---------------------------------------------------------------------------
// Styling helpers
// ---------------------------------------------------------------------------

const VARIANT_STYLES: Record<ToastVariant, string> = {
  success: 'bg-green-50 border-green-400 text-green-800',
  error: 'bg-red-50 border-red-400 text-red-800',
  info: 'bg-blue-50 border-blue-400 text-blue-800',
  warning: 'bg-amber-50 border-amber-400 text-amber-800',
}

const ICON_STYLES: Record<ToastVariant, string> = {
  success: 'text-green-500',
  error: 'text-red-500',
  info: 'text-blue-500',
  warning: 'text-amber-500',
}

function ToastIcon({ variant }: { variant: ToastVariant }) {
  if (variant === 'success') {
    return (
      <svg className={`w-5 h-5 shrink-0 ${ICON_STYLES.success}`} fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      </svg>
    )
  }
  if (variant === 'error') {
    return (
      <svg className={`w-5 h-5 shrink-0 ${ICON_STYLES.error}`} fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
      </svg>
    )
  }
  if (variant === 'warning') {
    return (
      <svg className={`w-5 h-5 shrink-0 ${ICON_STYLES.warning}`} fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
    )
  }
  // info
  return (
    <svg className={`w-5 h-5 shrink-0 ${ICON_STYLES.info}`} fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
    </svg>
  )
}

// ---------------------------------------------------------------------------
// Individual toast item
// ---------------------------------------------------------------------------

function ToastCard({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const delay = item.duration !== undefined ? item.duration : 4000
    if (delay > 0) {
      timerRef.current = setTimeout(onDismiss, delay)
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [item.duration, onDismiss])

  return (
    <div
      role="alert"
      className={`flex items-start gap-3 w-80 max-w-sm rounded-lg border px-4 py-3 shadow-lg
        transition-all duration-300 ease-in-out
        ${VARIANT_STYLES[item.variant]}`}
    >
      <ToastIcon variant={item.variant} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold leading-5 break-words">{item.title}</p>
        {item.message && (
          <p className="text-xs mt-0.5 opacity-80 break-words">{item.message}</p>
        )}
      </div>
      <button
        onClick={onDismiss}
        aria-label="Dismiss notification"
        className="shrink-0 text-current opacity-50 hover:opacity-80 transition-opacity"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

let _idCounter = 0
function nextId() {
  return `toast-${++_idCounter}`
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])

  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const add = useCallback((variant: ToastVariant, title: string, message?: string, duration?: number) => {
    const id = nextId()
    setItems((prev) => {
      // Cap the visible stack at 5
      const next = [...prev, { id, variant, title, message, duration }]
      return next.slice(-5)
    })
  }, [])

  const toast = {
    success: (title: string, message?: string, duration?: number) => add('success', title, message, duration),
    error: (title: string, message?: string, duration?: number) => add('error', title, message, duration),
    info: (title: string, message?: string, duration?: number) => add('info', title, message, duration),
    warning: (title: string, message?: string, duration?: number) => add('warning', title, message, duration),
  }

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {/* Toasts portal — fixed bottom-right */}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="fixed bottom-5 right-5 z-50 flex flex-col gap-2 items-end"
      >
        {items.map((item) => (
          <ToastCard key={item.id} item={item} onDismiss={() => dismiss(item.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast must be used inside <ToastProvider>')
  }
  return ctx
}
